import { CollapsibleSection } from './ui';

// T226 — the last three counters the observability ticket asked for.
//
// Each already existed and none was on a page. Spend was a MONTHLY figure on
// /analytics, which answers "am I near the budget" rather than "what did
// yesterday cost" — and those diverge exactly on the day a retry loop or a
// widened filter triples the call count. Scans existed as individual rows
// behind /scans, so "did it run every day this week" meant counting. Source
// errors were a status word with no count and no trend.

const fmtUsd = (n) => (n >= 1 ? `$${n.toFixed(2)}` : `${Math.round(n * 100)}¢`);
const dayLabel = (iso) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;

// A sparkline, not a chart: fourteen numbers do not need axes, and the question
// is "is today unlike the other thirteen".
function Spark({ days, valueOf, colorOf, title }) {
  const max = Math.max(...days.map(valueOf), 0);
  return (
    <div className="flex h-8 items-end gap-[3px]" role="img" aria-label={title}>
      {days.map((d) => {
        const v = valueOf(d);
        // A zero day must still be visible — an empty column and a missing
        // column look identical, and "nothing ran" is the signal that matters.
        const pct = max > 0 ? Math.max(6, Math.round((v / max) * 100)) : 6;
        return (
          <span
            key={d.date}
            title={`${dayLabel(d.date)}: ${title.includes('Spend') ? fmtUsd(v) : v}`}
            className={'w-full rounded-sm ' + (v > 0 ? colorOf(d) : 'bg-slate-800')}
            style={{ height: `${pct}%` }}
          />
        );
      })}
    </div>
  );
}

// How many erroring sources the list shows before it says it is truncating.
const ERRORS_SHOWN = 8;

export default function OpsCounters({ counters }) {
  if (!counters?.days?.length) return null;
  const { days, totals, errorsBySource } = counters;
  const today = days.at(-1);
  const quietDays = days.filter((d) => d.scans === 0).length;

  return (
    <CollapsibleSection
      id="ops-counters"
      title="Last 14 days"
      summary={`${totals.scans} scans · ${fmtUsd(totals.costUsd)} · ${errorsBySource.length} sources erroring`}
      hint="Scans and AI spend per local day, and which sources threw. Spend is every Claude call the app made on your behalf, priced at write time."
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <p className="mb-1 flex items-baseline justify-between text-xs">
            <span className="font-semibold text-slate-300">Scans</span>
            <span className="tabular-nums text-slate-500">
              {totals.scans} total{totals.failed > 0 && <span className="ml-1 text-red-400">· {totals.failed} failed</span>}
            </span>
          </p>
          <Spark
            days={days}
            valueOf={(d) => d.scans}
            colorOf={(d) => (d.failed > 0 ? 'bg-red-500/70' : 'bg-brand-600/70')}
            title="Scans per day"
          />
          <p className="mt-1 text-[11px] text-slate-500">
            {quietDays === 0
              ? 'Ran every day.'
              : `${quietDays} ${quietDays === 1 ? 'day' : 'days'} with no scan at all.`}
          </p>
        </div>

        <div>
          <p className="mb-1 flex items-baseline justify-between text-xs">
            <span className="font-semibold text-slate-300">AI spend</span>
            <span className="tabular-nums text-slate-500">{fmtUsd(totals.costUsd)} · {fmtUsd(totals.costPerDayUsd)}/day</span>
          </p>
          <Spark
            days={days}
            valueOf={(d) => d.costUsd}
            // Amber above three times the fortnight's daily average: the point
            // of a per-day figure is to catch the day that is not like the rest.
            colorOf={(d) => (d.costUsd > totals.costPerDayUsd * 3 ? 'bg-amber-500/70' : 'bg-brand-600/70')}
            title="Spend per day"
          />
          <p className="mt-1 text-[11px] text-slate-500">
            Today {fmtUsd(today.costUsd)}.
          </p>
        </div>
      </div>

      {errorsBySource.length > 0 && (
        <div className="mt-4">
          <p className="mb-1.5 text-xs font-semibold text-slate-300">
            Sources that threw
            {/* T290 — the summary above counts every erroring source and this
                list stops at eight with nothing saying so. On a bad morning the
                counter read "14 sources erroring" over a list of eight, and the
                six missing ones were the tail — the least-noticed and the most
                likely to have gone dark unremarked. */}
            {errorsBySource.length > ERRORS_SHOWN && (
              <span className="ml-1.5 font-normal text-slate-500">
                — showing {ERRORS_SHOWN} of {errorsBySource.length}
              </span>
            )}
          </p>
          <ul className="space-y-1 text-xs">
            {errorsBySource.slice(0, ERRORS_SHOWN).map((s) => (
              <li key={s.source} className="flex items-baseline justify-between gap-3">
                <span className="min-w-0 truncate text-slate-400">{s.source}</span>
                <span className="shrink-0 tabular-nums text-slate-500">
                  {/* The denominator is the point: 1 of 30 and 1 of 1 are
                      different problems and read identically without it. */}
                  <span className="text-red-400">{s.errors}</span> of {s.scans} scans
                </span>
              </li>
            ))}
          </ul>
          {errorsBySource[0]?.lastError && (
            <p className="mt-1.5 truncate text-[11px] text-slate-600" title={errorsBySource[0].lastError}>
              Latest: {errorsBySource[0].lastError}
            </p>
          )}
        </div>
      )}
    </CollapsibleSection>
  );
}
